import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import { Dictionary } from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { Button, Table } from "reactstrap";
import { useMeCore } from "../../../cores/me";
import { useRouterCore } from "../../../cores/router";
import { useSocialPostCore } from "../../../cores/socialPost";
import {
  SocialPostStatusOptions,
  SocialProviderNameMapper,
  SocialStoryStatusOptions,
} from "../../../cores/socialPost/config";
import { SOCIAL_POST_TYPES } from "../../../cores/socialPost/consts/socialPostType.constant";
import { SocialPost } from "../../../cores/socialPost/interfaces/api/get-social-posts";
import { IconMapper } from "../socialPost/selector/SocialPostSelector";
import {
  getSocialHistoryProviderIconKey,
  socialHistoryBrandSlug,
} from "./socialHistoryProviderIconKey";
import "./SocialHistoryTable.scss";

const providerNameMap = SocialProviderNameMapper as Dictionary<string>;

function labelForSocialProvider(raw: string | undefined): string {
  if (!raw) return "—";
  const byKey = providerNameMap[raw] || providerNameMap[raw.toLowerCase()];
  if (byKey) return byKey;
  const key = Object.keys(providerNameMap).find(
    (k) => k.toLowerCase() === raw.toLowerCase()
  );
  return key ? providerNameMap[key]! : raw;
}

function excerpt(text: string, max = 120): string {
  const t = (text || "").trim().replace(/\s+/g, " ");
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

interface Props {
  posts: SocialPost[];
  isDarkMode?: boolean;
  selectedIds: string[];
  onToggleSelect: (postId: string) => void;
  allVisibleSelected: boolean;
  onToggleSelectAll: () => void;
}

export function SocialHistoryTable({
  posts,
  isDarkMode = false,
  selectedIds,
  onToggleSelect,
  allVisibleSelected,
  onToggleSelectAll,
}: Props) {
  const selectAllRef = useRef<HTMLInputElement>(null);
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    const el = selectAllRef.current;
    if (el) {
      el.indeterminate = someSelected && !allVisibleSelected;
    }
  }, [someSelected, allVisibleSelected]);
  const intl = useIntl();
  const { company } = useMeCore();
  const { goToSocialPost, goToSocialStory } = useRouterCore();
  const { selectPost, removeAllImages, removeVideo, deleteSocialPost } =
    useSocialPostCore();

  const openPost = useCallback(
    (post: SocialPost) => {
      removeVideo();
      removeAllImages();
      selectPost(post);
      if (post.post.target === "post") {
        goToSocialPost("history");
      } else {
        goToSocialStory("history");
      }
    },
    [goToSocialPost, goToSocialStory, removeAllImages, removeVideo, selectPost]
  );

  const handleDeleteFromHistory = useCallback(
    (postId: string) => {
      const ok = window.confirm(
        intl.formatMessage({ id: "app.socialHistory.deleteConfirm" })
      );
      if (!ok || !company?.id) return;
      void deleteSocialPost(company.id, postId);
    },
    [company?.id, deleteSocialPost, intl]
  );

  const rows = useMemo(
    () =>
      posts.map((post) => {
        const p = post.post;
        const iconKey = getSocialHistoryProviderIconKey(p.socialProvider);
        const brandSlug = socialHistoryBrandSlug(iconKey);
        const networkIcon = iconKey
          ? IconMapper[iconKey]?.({ active: true, size: "sm" })
          : null;

        const statusOpts =
          p.target === "story" ? SocialStoryStatusOptions : SocialPostStatusOptions;
        const statusLabel =
          statusOpts.find((o) => o.value === String(p.status))?.label ??
          `— (${p.status})`;

        const when = p.postAt
          ? moment(p.postAt)
          : p.scheduleDate
          ? moment(p.scheduleDate)
          : null;
        const whenStr = when?.isValid()
          ? when.format("DD/MM/YYYY HH:mm")
          : "—";

        let typeLabel =
          p.target === "story"
            ? intl.formatMessage({ id: "app.socialHistory.targetStory" })
            : p.type === SOCIAL_POST_TYPES.VIDEO
            ? intl.formatMessage({ id: "app.socialHistory.typeVideo" })
            : p.type === SOCIAL_POST_TYPES.IMAGE
            ? intl.formatMessage({ id: "app.socialHistory.typeImage" })
            : intl.formatMessage({ id: "app.socialHistory.typePost" });

        return {
          post,
          brandSlug,
          networkIcon,
          networkLabel: labelForSocialProvider(p.socialProvider),
          statusLabel,
          whenStr,
          typeLabel,
          text: excerpt(p.text || ""),
        };
      }),
    [posts, intl]
  );

  return (
    <div
      className={cn("social-history-table-wrap", {
        "social-history-table-wrap--dark": isDarkMode,
      })}
    >
      <Table
        responsive
        hover
        borderless
        className="social-history-table mb-0"
      >
        <thead>
          <tr>
            <th
              scope="col"
              className="social-history-table__th-check text-center"
            >
              <div className="custom-control custom-checkbox d-inline-block mb-0">
                <input
                  ref={selectAllRef}
                  id="social-history-table-select-all"
                  type="checkbox"
                  className="custom-control-input"
                  checked={allVisibleSelected}
                  onChange={(e) => {
                    e.preventDefault();
                    onToggleSelectAll();
                  }}
                  title={intl.formatMessage({ id: "app.socialHistory.selectVisible" })}
                  aria-label={intl.formatMessage({
                    id: "app.socialHistory.selectVisible",
                  })}
                />
                <label
                  className="custom-control-label sr-only"
                  htmlFor="social-history-table-select-all"
                >
                  {intl.formatMessage({ id: "app.socialHistory.selectVisible" })}
                </label>
              </div>
            </th>
            <th scope="col" className="social-history-table__th-date">
              {intl.formatMessage({ id: "app.socialHistory.tableColDate" })}
            </th>
            <th scope="col" className="social-history-table__th-network">
              {intl.formatMessage({ id: "app.socialHistory.tableColNetwork" })}
            </th>
            <th scope="col" className="social-history-table__th-status">
              {intl.formatMessage({ id: "app.socialHistory.tableColStatus" })}
            </th>
            <th scope="col" className="social-history-table__th-type d-none d-md-table-cell">
              {intl.formatMessage({ id: "app.socialHistory.tableColType" })}
            </th>
            <th scope="col" className="social-history-table__th-text">
              {intl.formatMessage({ id: "app.socialHistory.tableColContent" })}
            </th>
            <th scope="col" className="social-history-table__th-action text-end">
              <span className="sr-only">
                {intl.formatMessage({ id: "app.socialHistory.tableColAction" })}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(
            ({
              post,
              brandSlug,
              networkIcon,
              networkLabel,
              statusLabel,
              whenStr,
              typeLabel,
              text,
            }) => (
              <tr
                key={post.post.id}
                className={cn("social-history-table__row", `social-history-table__row--${brandSlug}`)}
              >
                <td className="social-history-table__td-check text-center align-middle">
                  <input
                    type="checkbox"
                    className="social-history-table__row-check"
                    checked={selectedIds.includes(String(post.post.id))}
                    onChange={() => onToggleSelect(String(post.post.id))}
                    title={intl.formatMessage({ id: "app.socialHistory.selectOneTitle" })}
                    aria-label={intl.formatMessage({
                      id: "app.socialHistory.selectOneTitle",
                    })}
                  />
                </td>
                <td className="social-history-table__td-date text-nowrap">
                  {whenStr}
                </td>
                <td className="social-history-table__td-network">
                  <span className="social-history-table__network">
                    <span className="social-history-table__network-icon">
                      {networkIcon ?? (
                        <FontAwesomeIcon icon={["fas", "share-nodes"]} />
                      )}
                    </span>
                    <span className="social-history-table__network-name">
                      {networkLabel}
                    </span>
                  </span>
                </td>
                <td className="social-history-table__td-status">
                  <span className="social-history-table__status-pill">{statusLabel}</span>
                </td>
                <td className="social-history-table__td-type d-none d-md-table-cell">
                  {typeLabel}
                </td>
                <td className="social-history-table__td-text">
                  <span className="social-history-table__text-preview" title={post.post.text || ""}>
                    {text || "—"}
                  </span>
                </td>
                <td className="social-history-table__td-action text-nowrap text-end">
                  <div className="social-history-table__action-btns">
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      className="social-history-table__open-btn"
                      type="button"
                      onClick={() => openPost(post)}
                    >
                      {intl.formatMessage({ id: "app.socialHistory.tableOpen" })}
                    </Button>
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      className="social-history-table__delete-btn"
                      type="button"
                      title={intl.formatMessage({
                        id: "app.socialHistory.deleteTitle",
                      })}
                      aria-label={intl.formatMessage({
                        id: "app.socialHistory.deleteTitle",
                      })}
                      onClick={() =>
                        handleDeleteFromHistory(String(post.post.id))
                      }
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                  </div>
                </td>
              </tr>
            )
          )}
        </tbody>
      </Table>
    </div>
  );
}
